import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Building2, Activity, ShieldCheck, ShieldAlert, Database, HardDrive, Mail,
  Users as UsersIcon, FolderKanban, Inbox, Plus, Search, Loader2, Check,
  X, ArrowRight, CheckCircle2, AlertTriangle, Target, Wrench,
} from "lucide-react";
import { toast } from "sonner";
import CommandCenter from "@/components/ops/CommandCenter";
import ActivityCenter from "@/components/ops/ActivityCenter";
import { usePlatformFetch, LoadError } from "@/components/ops/_primitives";

function Chip({ state, label }) {
  const map = {
    ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    red: "bg-red-500/15 text-red-300 border-red-500/30",
    down: "bg-red-500/15 text-red-300 border-red-500/30",
    missing: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    local: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    misconfigured: "bg-red-500/15 text-red-300 border-red-500/30",
  };
  return <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded border ${map[state] || "border-[var(--fop-border)]"}`}>{label || state}</span>;
}

function Stat({ label, value, sub }) {
  return (
    <div className="fop-surface-2 rounded-lg p-3 border border-[var(--fop-border)]">
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">{label}</div>
      <div className="font-display text-2xl text-[var(--fop-text)] mt-0.5">{value}</div>
      {sub && <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-0.5">{sub}</div>}
    </div>
  );
}

export default function PlatformConsole() {
  const { user } = useAuth();
  const [tab, setTab] = useState("command");

  if (!user?.is_platform_admin) {
    return (
      <div className="p-10 max-w-xl mx-auto text-center" data-testid="ops-console-denied">
        <ShieldAlert size={36} className="text-amber-400 mx-auto mb-3" />
        <h1 className="font-display text-2xl mb-2">Operations Console</h1>
        <p className="text-sm text-[var(--fop-text-3)]">ForgedOps staff access only. Your account is not authorised to manage customer companies.</p>
        <Link to="/portfolio" className="btn btn-ghost text-xs mt-4 inline-flex items-center gap-1.5" data-testid="ops-back-to-portfolio">
          <ArrowRight size={12} /> Go to tenant workspace
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto overflow-x-hidden" data-testid="platform-console-page">
      <div className="fop-surface-2 p-4 sm:p-5 rounded-lg mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">ForgedOps · Operations</div>
          <h1 className="font-display text-2xl text-[var(--fop-text)] inline-flex items-center gap-2">
            <Building2 size={20} className="text-[var(--fop-primary)]" /> Command Center
          </h1>
          <p className="text-xs text-[var(--fop-text-3)] mt-1 max-w-2xl">One screen. The action you must take next.</p>
        </div>
        <div
          className="flex w-full sm:w-auto items-center gap-1 bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded-md p-1 overflow-x-auto no-scrollbar"
          data-testid="ops-tabs"
        >
          {[
            { id: "command", label: "Command", icon: <Target size={11} />, testid: "tab-command" },
            { id: "tenants", label: "Customers", icon: <Building2 size={11} />, testid: "tab-tenants" },
            { id: "diagnostics", label: "Diagnostics", icon: <Wrench size={11} />, testid: "tab-diagnostics" },
            { id: "onboard", label: "+ Onboard", icon: <Plus size={11} />, testid: "tab-onboard" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={t.testid}
              className={`shrink-0 whitespace-nowrap px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-wider rounded inline-flex items-center gap-1.5 ${tab === t.id ? "bg-[var(--fop-primary)] text-black" : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"}`}
            >{t.icon}{t.label}</button>
          ))}
        </div>
      </div>

      {tab === "command" && <CommandCenter onOpenOnboarding={() => setTab("onboard")} />}
      {tab === "tenants" && <CustomerManagement onOpenOnboarding={() => setTab("onboard")} />}
      {tab === "diagnostics" && <Diagnostics />}
      {tab === "onboard" && <OnboardingWizard onDone={() => setTab("tenants")} />}
    </div>
  );
}

function HealthDashboard() {
  const { data, error: healthErr, retry: retryHealth } = usePlatformFetch("/platform/health");
  const { data: summariesData, error: summariesErr } = usePlatformFetch("/platform/tenant_health");
  const { data: successData, error: successErr } = usePlatformFetch("/platform/customer_success");
  const summaries = useMemo(() => summariesData || [], [summariesData]);
  const success = successData || {};
  const [cutoffIso] = useState(() => new Date(Date.now() - 30 * 86400 * 1000).toISOString());
  const newTenants = useMemo(
    () => summaries.filter((row) => (row?.tenant?.created_at || "") >= cutoffIso).length,
    [summaries, cutoffIso],
  );
  const atRisk = useMemo(() => summaries.filter((r) => r.health?.status === "at_risk").length, [summaries]);
  const needsAttention = useMemo(() => summaries.filter((r) => r.health?.status === "needs_attention").length, [summaries]);
  const err = healthErr || summariesErr || successErr;
  if (err) return <LoadError error={err} retry={retryHealth} label="Health dashboard" />;
  if (!data) return <div className="p-8 font-mono text-xs text-[var(--fop-text-3)] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
  const c = data.components || {};
  const f = data.fleet || {};
  const a = data.activity_24h || {};
  const reason = data.overall === "green" ? "Platform Healthy" : data.overall === "amber" ? "Attention Required — see system health below" : "Critical Issues Detected — see system health below";
  return (
    <div className="space-y-5" data-testid="health-dashboard">
      {/* Platform Status hero */}
      <div className={`rounded-lg p-5 border ${data.overall === "green" ? "border-emerald-500/30 bg-emerald-500/5" : data.overall === "amber" ? "border-amber-500/30 bg-amber-500/5" : "border-red-500/30 bg-red-500/5"}`} data-testid="platform-status-hero">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-80">Platform Status</div>
        <div className={`font-display text-3xl mt-1 ${data.overall === "green" ? "text-emerald-300" : data.overall === "amber" ? "text-amber-300" : "text-red-300"}`}>{data.overall.toUpperCase()}</div>
        <div className="text-sm text-[var(--fop-text-2)] mt-1">{reason}</div>
      </div>

      <Section title="Customer Health" icon={<Building2 size={13} />}>
        <Stat label="Total customers" value={f.tenants?.total ?? 0} sub={`${newTenants} new (30d)`} />
        <Stat label="Active" value={f.tenants?.active ?? 0} />
        <Stat label="At risk" value={atRisk} sub="needs intervention" />
        <Stat label="Needs attention" value={needsAttention} />
      </Section>

      <Section title="Customer Success" icon={<UsersIcon size={13} />}>
        <Stat label="Invites pending" value={(success.invite_pending || []).length} />
        <Stat label="Never logged in" value={(success.never_logged_in || []).length} />
        <Stat label="No projects" value={(success.no_projects || []).length} />
        <Stat label="Inactive ≥14d" value={(success.inactive_14d || []).length + (success.inactive_30d || []).length + (success.inactive_60d || []).length} sub="follow-up needed" />
      </Section>

      <Section title="Platform Usage · 24h" icon={<Activity size={13} />}>
        <Stat label="Users signed in 24h" value={f.users?.logged_in_24h ?? 0} sub={`${f.users?.active ?? 0} active accounts`} />
        <Stat label="Email events" value={a.email_events ?? 0} />
        <Stat label="Errors / warnings" value={a.errors_or_warnings ?? 0} />
        <Stat label="Upload rejections" value={a.upload_rejections ?? 0} />
      </Section>

      <div data-testid="health-components">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)] mb-2 inline-flex items-center gap-1.5"><ShieldCheck size={11} /> System Health · {data.overall?.toUpperCase()}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Component icon={<Database size={13} />} label="Database" state={c.database?.state} sub={`${c.database?.latency_ms ?? "—"} ms`} />
          <Component icon={<HardDrive size={13} />} label="Storage" state={c.storage?.state} sub={`backend · ${c.storage?.backend || "—"}`} />
          <Component icon={<Mail size={13} />} label="Email" state={c.email?.state} sub={c.email?.state === "ok" ? "Resend live" : "RESEND_API_KEY missing"} />
          <Component icon={<ShieldCheck size={13} />} label="Webhook secret" state={c.webhook_secret?.state} sub={c.webhook_secret?.state === "ok" ? "Signed" : "INBOUND_WEBHOOK_SECRET missing"} />
        </div>
      </div>
    </div>
  );
}

function CustomerSuccess({ onOpenTenant }) {
  const { data, error, retry } = usePlatformFetch("/platform/customer_success");
  if (error) return <LoadError error={error} retry={retry} label="Customer success" />;
  if (!data) return <div className="p-8 font-mono text-xs text-[var(--fop-text-3)] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
  const groups = [
    { key: "never_logged_in", label: "Never logged in" },
    { key: "invite_pending", label: "Invite pending" },
    { key: "no_projects", label: "No projects" },
    { key: "no_uploads", label: "No plan uploads" },
    { key: "inactive_14d", label: "Inactive ≥14 days" },
    { key: "inactive_30d", label: "Inactive ≥30 days" },
    { key: "inactive_60d", label: "Inactive ≥60 days" },
    { key: "at_risk", label: "At risk (score <50)" },
  ];
  return (
    <div className="space-y-3" data-testid="customer-success">
      {groups.map((g) => {
        const items = data[g.key] || [];
        return (
          <div key={g.key} className="fop-surface-2 rounded-lg overflow-hidden" data-testid={`success-group-${g.key}`}>
            <div className="px-4 py-2 border-b border-[var(--fop-border)] flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-2)]">{g.label}</div>
              <span className="text-[10px] font-mono text-[var(--fop-text-4)]">{items.length}</span>
            </div>
            {items.length === 0 ? (
              <div className="px-4 py-3 text-[11px] text-[var(--fop-text-4)] italic">No customers in this bucket.</div>
            ) : (
              <table className="w-full text-xs">
                <tbody>
                  {items.slice(0, 10).map((it) => (
                    <tr key={it.tenant_id} className="border-t border-[var(--fop-border)]" data-testid={`success-row-${it.tenant_id}`}>
                      <td className="px-3 py-1.5 w-1/3">
                        <Link to={`/platform/tenants/${it.tenant_id}`} className="text-[var(--fop-text)] hover:text-[var(--fop-primary)]">{it.tenant_name}</Link>
                      </td>
                      <td className="px-3 py-1.5 text-[10px] font-mono text-[var(--fop-text-4)]">{it.owner_email || "—"}</td>
                      <td className="px-3 py-1.5">
                        <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded border ${it.health_score < 50 ? "border-red-500/30 text-red-300 bg-red-500/10" : "border-amber-500/30 text-amber-300 bg-amber-500/10"}`}>
                          {it.health_score} · {it.health_status?.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-[10px] font-mono text-[var(--fop-text-4)] text-right">
                        {it.days_since_login != null ? `${Math.round(it.days_since_login)}d since login` : "no login"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div data-testid={`section-${title.toLowerCase().split(" ")[0]}`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)] mb-2 inline-flex items-center gap-1.5">{icon} {title}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{children}</div>
    </div>
  );
}

function Component({ icon, label, state, sub }) {
  return (
    <div className="fop-surface-2 rounded-lg p-3 border border-[var(--fop-border)]">
      <div className="inline-flex items-center gap-1.5 text-[var(--fop-text-2)] mb-1">{icon}<span className="text-[10px] font-mono uppercase tracking-wider">{label}</span></div>
      <Chip state={state} />
      <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-1">{sub}</div>
    </div>
  );
}

function CustomerManagement({ onOpenOnboarding }) {
  const { data, error, retry } = usePlatformFetch("/platform/tenant_health");
  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const filtered = useMemo(() => {
    const s = (q || "").trim().toLowerCase();
    return rows.filter((row) => {
      const t = row.tenant || {};
      if (statusFilter && t.status !== statusFilter) return false;
      if (!s) return true;
      return [t.display_name, t.slug, t.contact_email, row.owner?.email, row.owner?.name]
        .some((v) => (v || "").toLowerCase().includes(s));
    });
  }, [rows, q, statusFilter]);

  const statusCounts = useMemo(() => {
    const out = { active: 0, disabled: 0, archived: 0 };
    for (const r of rows) out[r.tenant?.status] = (out[r.tenant?.status] || 0) + 1;
    return out;
  }, [rows]);

  return (
    <div className="space-y-3" data-testid="customer-management">
      <div className="fop-surface-2 rounded-lg p-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--fop-text-4)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, slug, owner email" className="w-full pl-7 pr-3 py-1.5 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-xs" data-testid="tenant-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="tenant-status-filter" className="px-2 py-1.5 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-xs">
          <option value="">{`All statuses (${rows.length})`}</option>
          <option value="active">{`Active (${statusCounts.active || 0})`}</option>
          <option value="disabled">{`Suspended (${statusCounts.disabled || 0})`}</option>
          <option value="archived">{`Archived (${statusCounts.archived || 0})`}</option>
        </select>
        <button onClick={onOpenOnboarding} className="btn btn-primary text-xs inline-flex items-center gap-1.5" data-testid="open-onboarding">
          <Plus size={11} /> Onboard new
        </button>
      </div>

      {error ? (
        <LoadError error={error} retry={retry} label="Customer list" />
      ) : !data ? (
        <div className="p-8 font-mono text-xs text-[var(--fop-text-3)] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : (
        <div className="fop-surface-2 rounded-lg overflow-hidden" data-testid="tenant-list">
          <table className="w-full text-xs">
            <thead className="bg-[var(--fop-surface)] text-[var(--fop-text-3)]">
              <tr className="text-left">
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Customer</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Owner</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Health</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">MASCI ready</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Users</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Projects</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Status</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Last login</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const t = row.tenant || {};
                const h = row.health || {};
                const rd = row.readiness || {};
                const dsl = h.days_since_login;
                const hColor = h.status === "excellent" ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" :
                                 h.status === "healthy" ? "text-emerald-300 bg-emerald-500/5 border-emerald-500/20" :
                                 h.status === "needs_attention" ? "text-amber-300 bg-amber-500/10 border-amber-500/30" :
                                 "text-red-300 bg-red-500/10 border-red-500/30";
                const rColor = rd.status === "ready" ? "text-emerald-300" : rd.status === "partially_ready" ? "text-amber-300" : "text-red-300";
                return (
                  <tr key={t.id} className="border-t border-[var(--fop-border)] hover:bg-[var(--fop-surface)]/40 transition-colors" data-testid={`tenant-row-${t.id}`}>
                    <td className="px-3 py-2">
                      <Link to={`/platform/tenants/${t.id}`} className="font-display text-sm text-[var(--fop-text)] hover:text-[var(--fop-primary)]">{t.display_name}</Link>
                      <div className="text-[10px] font-mono text-[var(--fop-text-4)]">{t.slug}</div>
                    </td>
                    <td className="px-3 py-2 text-[var(--fop-text-2)]">{row.owner?.email || "—"}<div className="text-[10px] font-mono text-[var(--fop-text-4)]">{row.owner?.name || ""}</div></td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded border ${hColor}`} data-testid={`tenant-health-${t.id}`}>
                        {h.score ?? "—"} · {(h.status || "").replace("_", " ")}
                      </span>
                    </td>
                    <td className={`px-3 py-2 font-mono text-[10px] ${rColor}`} title={(rd.checks || []).filter((c) => !c.ok).map((c) => `✗ ${c.label}`).join("\n") || "All readiness checks passed"}>{rd.score ?? "—"} · {(rd.status || "").replace("_", " ")}</td>
                    <td className="px-3 py-2 font-mono">{row.users}</td>
                    <td className="px-3 py-2 font-mono">{row.projects}</td>
                    <td className="px-3 py-2"><Chip state={t.status === "active" ? "ok" : "missing"} label={t.status} /></td>
                    <td className="px-3 py-2 text-[10px] font-mono text-[var(--fop-text-4)]">{dsl != null ? `${Math.round(dsl)}d ago` : "—"}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-[var(--fop-text-3)]">No tenants match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OnboardingWizard({ onDone }) {
  const [form, setForm] = useState({
    company_name: "", company_slug: "", contact_email: "", contact_phone: "",
    website: "", primary_color: "", secondary_color: "",
    initial_owner_email: "", initial_owner_name: "",
  });
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const slug = (form.company_slug || form.company_name).toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

  const emailLooksValid = (e) => /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test((e || "").trim()) && !/\.(local|test|invalid|example)$/i.test((e || "").trim());

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        company_name: form.company_name.trim(),
        company_slug: slug,
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim() || null,
        website: form.website.trim() || null,
        primary_color: form.primary_color || null,
        secondary_color: form.secondary_color || null,
        initial_owner_email: form.initial_owner_email.trim(),
        initial_owner_name: form.initial_owner_name.trim() || null,
      };
      const r = await api.post("/platform/tenants", payload);
      setResult(r.data);
      setStep(4);
      toast.success("Tenant created and owner invited");
    } catch (e) {
      const d = e?.response?.data?.detail;
      let msg = "Onboarding failed";
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d) && d.length) {
        const first = d[0];
        const field = (first?.loc || []).filter((p) => p !== "body").join(".");
        msg = `${field ? field + ": " : ""}${first?.msg || "Validation failed"}`;
      }
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const next = () => {
    if (step === 1) {
      if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
      if (!emailLooksValid(form.contact_email)) {
        toast.error("Enter a valid contact email (no .local/.test/.example TLDs)"); return;
      }
    }
    if (step === 2 && !emailLooksValid(form.initial_owner_email)) {
      toast.error("Enter a valid owner email (no .local/.test/.example TLDs)"); return;
    }
    setStep((s) => Math.min(s + 1, 4));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 1));

  return (
    <div className="max-w-2xl mx-auto" data-testid="onboarding-wizard">
      <div className="fop-surface-2 rounded-lg p-5 border border-[var(--fop-border-hi)]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg">Onboard new customer</h2>
          <div className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
            Step {step} of 4
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-3" data-testid="step-company">
            <Field label="Company name *" value={form.company_name} onChange={(v) => set("company_name", v)} testid="w-company-name" placeholder="MASCI General Contractors" />
            <Field label="Slug (URL-safe)" value={slug} onChange={(v) => set("company_slug", v)} testid="w-company-slug" placeholder="masci-gc" mono />
            <Field label="Primary contact email *" value={form.contact_email} onChange={(v) => set("contact_email", v)} testid="w-contact-email" placeholder="ops@masci.com" type="email" />
            <Field label="Phone" value={form.contact_phone} onChange={(v) => set("contact_phone", v)} testid="w-contact-phone" placeholder="+1 813 555 0100" />
            <Field label="Website" value={form.website} onChange={(v) => set("website", v)} testid="w-website" placeholder="https://masci.com" />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="step-owner">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">Owner invite</div>
            <Field label="Owner email *" value={form.initial_owner_email} onChange={(v) => set("initial_owner_email", v)} testid="w-owner-email" placeholder="founder@masci.com" type="email" />
            <Field label="Owner name" value={form.initial_owner_name} onChange={(v) => set("initial_owner_name", v)} testid="w-owner-name" placeholder="Jane Smith" />
            <p className="text-[11px] text-[var(--fop-text-4)] italic">An invitation email with an accept-invite link will be sent automatically on completion.</p>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="step-branding">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">Branding (optional)</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Primary color" value={form.primary_color} onChange={(v) => set("primary_color", v)} testid="w-primary-color" placeholder="#F97316" />
              <Field label="Secondary color" value={form.secondary_color} onChange={(v) => set("secondary_color", v)} testid="w-secondary-color" placeholder="#0D1115" />
            </div>
            <div className="fop-surface rounded-md p-3 mt-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-2">Review</div>
              <KV k="Company" v={form.company_name} />
              <KV k="Slug" v={slug} />
              <KV k="Contact" v={form.contact_email} />
              <KV k="Owner" v={`${form.initial_owner_name} <${form.initial_owner_email}>`} />
            </div>
          </div>
        )}

        {step === 4 && result && (
          <div className="py-2" data-testid="step-success">
            <div className="text-center mb-5">
              <CheckCircle2 size={36} className="mx-auto text-emerald-400 mb-3" />
              <h3 className="font-display text-xl mb-1">Tenant created successfully</h3>
              <div className="text-xs text-[var(--fop-text-3)] space-y-0.5">
                <div>✓ Owner invited · <span className="font-mono">{result?.owner?.email || form.initial_owner_email}</span></div>
                <div>✓ Workspace active · <span className="font-mono">{result?.tenant?.slug || slug}</span></div>
                <div>✓ Branding configured</div>
              </div>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)] mb-2">Next steps</div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <Link to={`/platform/tenants/${result?.tenant?.id}`} className="btn btn-primary text-xs inline-flex items-center justify-center gap-1.5" data-testid="next-open-customer">
                <Building2 size={11} /> Open customer dashboard
              </Link>
              <a href={result?.invitation?.url} target="_blank" rel="noreferrer" className="btn btn-ghost text-xs inline-flex items-center justify-center gap-1.5" data-testid="next-copy-invite">
                <Mail size={11} /> Owner invite link
              </a>
              <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}${result?.invitation?.url || ""}`); toast.success("Invite link copied"); }} className="btn btn-ghost text-xs inline-flex items-center justify-center gap-1.5" data-testid="next-share-link">
                <Search size={11} /> Copy invite link
              </button>
              <Link to="/platform" onClick={onDone} className="btn btn-ghost text-xs inline-flex items-center justify-center gap-1.5" data-testid="next-back-list">
                <UsersIcon size={11} /> Customer list
              </Link>
            </div>
            <p className="text-[10px] font-mono text-[var(--fop-text-4)] text-center">
              Owner can sign in via the invite link · <span className="text-[var(--fop-text-3)]">{result?.invitation?.expires_at?.slice(0, 16)}</span>
            </p>
          </div>
        )}

        {step < 4 && (
          <div className="mt-5 flex items-center justify-between">
            <button onClick={prev} disabled={step === 1 || submitting} className="btn btn-ghost text-xs disabled:opacity-50" data-testid="wizard-back">Back</button>
            {step < 3 ? (
              <button onClick={next} className="btn btn-primary text-xs inline-flex items-center gap-1.5" data-testid="wizard-next">Next <ArrowRight size={11} /></button>
            ) : (
              <button onClick={submit} disabled={submitting} className="btn btn-primary text-xs inline-flex items-center gap-1.5" data-testid="wizard-submit">
                {submitting ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Create tenant &amp; invite owner
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder, type = "text", mono }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className={`w-full px-3 py-2 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm text-[var(--fop-text)] ${mono ? "font-mono" : ""}`}
      />
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-center justify-between text-[11px] py-0.5">
      <span className="font-mono uppercase tracking-wider text-[var(--fop-text-4)]">{k}</span>
      <span className="text-[var(--fop-text-2)] truncate ml-2">{v || "—"}</span>
    </div>
  );
}



/* ────────────────────────────────────────────────────────────────────
   Diagnostics — collapsible deep-dive container.
   Default state: COLLAPSED. The Command tab handles the 5-second answer.
   This screen exists for the rare cases an operator needs raw data.
   ───────────────────────────────────────────────────────────────── */

function Diagnostics() {
  const [open, setOpen] = useState(null);
  const items = [
    { id: "activity", label: "Activity feed", help: "Cross-tenant audit timeline. Use for forensics.", node: <ActivityCenter /> },
    { id: "health", label: "Health dashboard", help: "Legacy fleet rollup. Most of this is now on the Command screen.", node: <HealthDashboard /> },
    { id: "success", label: "Customer success buckets", help: "Raw bucket lists (never logged in, no projects, …). Click a tenant to drill in.", node: <CustomerSuccess onOpenTenant={() => {}} /> },
  ];
  return (
    <div className="space-y-2" data-testid="diagnostics">
      <div className="fop-surface-2 rounded-lg p-4 border border-[var(--fop-border)]">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">Diagnostics</div>
        <p className="text-[12px] text-[var(--fop-text-3)] mt-1">Deep-dive raw data. If you opened this tab and didn&apos;t need to, the Command screen failed you — please file a discovery.</p>
      </div>
      {items.map((it) => (
        <div key={it.id} className="fop-surface-2 rounded-lg border border-[var(--fop-border)] overflow-hidden" data-testid={`diag-${it.id}`}>
          <button onClick={() => setOpen(open === it.id ? null : it.id)} data-testid={`diag-toggle-${it.id}`}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--fop-surface)] transition">
            <div className="text-left">
              <div className="font-mono text-[11px] uppercase tracking-wider text-[var(--fop-text-2)]">{it.label}</div>
              <div className="text-[11px] text-[var(--fop-text-4)] mt-0.5">{it.help}</div>
            </div>
            <ArrowRight size={14} className={`text-[var(--fop-text-3)] transition ${open === it.id ? "rotate-90 text-[var(--fop-primary)]" : ""}`} />
          </button>
          {open === it.id && <div className="border-t border-[var(--fop-border)] p-4">{it.node}</div>}
        </div>
      ))}
    </div>
  );
}
